import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Toast } from "primereact/toast";
import BeatLoader from "react-spinners/BeatLoader";
import {
  FaEnvelope,
  FaEye,
  FaEyeSlash,
  FaLock,
} from "react-icons/fa";
import AuthService from "../../modules/Admin/services/AuthService";
import BrandLogo from "../components/BrandLogo";
import { PRODUCT_DISPLAY_NAME } from "../constants/branding";
import heroImg from "../../assets/img/bg-smart-home-1.jpg";

const CRM_FEATURES = [
  { icon: "fab fa-facebook", tone: "#2563eb", title: "Meta Lead Ads", desc: "Facebook & Instagram webhook capture" },
  { icon: "fas fa-file-excel", tone: "#16a34a", title: "Excel Import", desc: "Bulk upload with smart mapping" },
  { icon: "fas fa-tasks", tone: "#7c3aed", title: "Lead Assignment", desc: "Agent routing & follow-up reminders" },
  { icon: "fas fa-columns", tone: "#0891b2", title: "Kanban Pipeline", desc: "Visual stages hot to closed" },
  { icon: "fas fa-building", tone: "#0d9488", title: "Property Listing", desc: "Inventory, units & bookings" },
  { icon: "fas fa-chart-line", tone: "#db2777", title: "Dashboard & Reports", desc: "KPIs & team analytics" },
  { icon: "fas fa-sitemap", tone: "#4f46e5", title: "Multi-tenant", desc: "Isolated workspace per agency" },
  { icon: "fas fa-user-shield", tone: "#9333ea", title: "Role-based Access", desc: "Admin & employee permissions" },
  { icon: "fas fa-map-marker-alt", tone: "#dc2626", title: "Team Attendance", desc: "Geo-fenced field check-in" },
  { icon: "fas fa-user-check", tone: "#059669", title: "Buyer CRM", desc: "Profiles, interests & deals" },
  { icon: "fas fa-handshake", tone: "#d97706", title: "Builders & Partners", desc: "Commissions & tie-ups" },
  { icon: "fas fa-bell", tone: "#ea580c", title: "Notifications", desc: "Alerts for leads & follow-ups" },
];

const MOBILE_PILLS = [
  { icon: "fab fa-facebook", label: "Meta leads" },
  { icon: "fas fa-columns", label: "Kanban" },
  { icon: "fas fa-chart-line", label: "Reports" },
];

const Roles = {
  SuperAdmin: 1,
  Admin: 2,
  Employee: 4,
};

function LoginForm() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const toast = React.useRef(null);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    const { email: savedEmail } = AuthService.getRememberedCredentials();
    if (savedEmail) {
      setEmail(savedEmail);
      setRememberMe(true);
    }
    localStorage.removeItem("password");
  }, []);

  function handleSubmit(event) {
    event.preventDefault();
    setLoading(true);

    AuthService.login(email, password, rememberMe, navigate, Roles)
      .catch((err) => {
        if (toast.current) {
          toast.current.show({
            severity: "error",
            summary: "Invalid Username or Password",
            detail: "Please enter a valid username or password.",
            life: 3000,
          });
        }
        console.error("Login failed", err);
      })
      .finally(() => {
        setLoading(false);
      });
  }

  return (
    <div className="auth-split">
      <Toast ref={toast} />

      <aside
        className="auth-hero"
        style={{ backgroundImage: `url(${heroImg})` }}
      >
        <div className="auth-hero-overlay" />
        <div className="auth-hero-content">
          <div className="auth-brand auth-animate-in">
            <BrandLogo className="auth-brand-logo" size={40} />
            <span className="auth-brand-name">{PRODUCT_DISPLAY_NAME}</span>
          </div>

          <div className="auth-hero-text auth-animate-in auth-animate-delay-1">
            <p className="auth-proof-line">Built for real estate agencies &amp; builders</p>
            <h1>
              Turn leads into closings,
              <br />
              <span className="accent">faster.</span>
            </h1>
            <p>
              Capture from Meta &amp; Excel, assign to your team, track pipeline
              stages, and manage properties — all in one CRM.
            </p>
          </div>

          <div className="auth-feature-section auth-feature-section--dark auth-animate-in auth-animate-delay-2">
            <h2 className="auth-feature-heading auth-feature-heading--dark">
              <i className="fas fa-layer-group" aria-hidden="true" />
              Platform features
            </h2>
            <div className="auth-feature-grid auth-feature-grid--dark">
              {CRM_FEATURES.map((feature, index) => (
                <div
                  key={feature.title}
                  className="auth-feature-card auth-feature-card--dark auth-animate-in"
                  style={{ animationDelay: `${0.28 + index * 0.04}s` }}
                >
                  <span
                    className="auth-feature-icon auth-feature-icon--dark"
                    style={{
                      color: feature.tone,
                      backgroundColor: `${feature.tone}1a`,
                    }}
                  >
                    <i className={feature.icon} aria-hidden="true" />
                  </span>
                  <div className="auth-feature-copy">
                    <strong>{feature.title}</strong>
                    <span>{feature.desc}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </aside>

      <section className="auth-form-panel">
        <div className="auth-mobile-hero">
          <div className="auth-brand auth-brand--mobile">
            <BrandLogo className="auth-brand-logo" size={36} />
            <span className="auth-brand-name auth-brand-name--mobile">{PRODUCT_DISPLAY_NAME}</span>
          </div>
          <p className="auth-mobile-tagline">
            Turn leads into closings, <span className="accent">faster.</span>
          </p>
          <div className="auth-mobile-pills">
            {MOBILE_PILLS.map((pill) => (
              <span key={pill.label} className="auth-mobile-pill">
                <i className={pill.icon} aria-hidden="true" />
                {pill.label}
              </span>
            ))}
          </div>
        </div>

        <div className="auth-form-wrap">
          <div className="auth-form-brand auth-animate-in">
            <BrandLogo className="auth-form-brand-logo" size={36} />
            <span>{PRODUCT_DISPLAY_NAME}</span>
          </div>

          <div className="auth-form-header auth-animate-in auth-animate-delay-1">
            <h2>Welcome back</h2>
            <p>Please sign in to your account to continue.</p>
          </div>

          <form role="form" onSubmit={handleSubmit} className="auth-form auth-animate-in auth-animate-delay-2">
            <label className="auth-field">
              <span className="auth-field-label">Email address</span>
              <span className="auth-input">
                <FaEnvelope className="auth-input-icon" />
                <input
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  type="email"
                  id="username"
                  name="username"
                  placeholder="you@company.com"
                  required
                />
              </span>
            </label>

            <label className="auth-field">
              <span className="auth-field-label">Password</span>
              <span className="auth-input">
                <FaLock className="auth-input-icon" />
                <input
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  type={showPassword ? "text" : "password"}
                  id="password"
                  name="password"
                  placeholder="Enter your password"
                  required
                />
                <button
                  type="button"
                  className="auth-input-toggle"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label="Toggle password visibility"
                >
                  {showPassword ? <FaEyeSlash /> : <FaEye />}
                </button>
              </span>
            </label>

            <div className="auth-row">
              <label className="auth-remember">
                <input
                  type="checkbox"
                  id="rememberMe"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                />
                <span>Remember me</span>
              </label>
            </div>

            <button type="submit" className="auth-submit" disabled={loading}>
              {loading ? (
                <span className="auth-submit-loading">
                  <BeatLoader color="#ffffff" size={8} /> Signing in...
                </span>
              ) : (
                "Sign in"
              )}
            </button>

            <p className="auth-signup">
              Don&apos;t have an account?{" "}
              <a href="mailto:support@nextfollowup.in?subject=Account%20access%20request">
                Contact your administrator
              </a>
              {" · "}
              <Link to="/home" className="auth-inline-link">
                Explore the product
              </Link>
            </p>
          </form>

          <footer className="auth-footer auth-animate-in auth-animate-delay-3">
            © {new Date().getFullYear()} {PRODUCT_DISPLAY_NAME}. All rights reserved.
          </footer>
        </div>
      </section>
    </div>
  );
}

export default LoginForm;
